import React, { useState } from 'react'
import { FilterType, FREE_TEXT_FILTER_TYPES, NodeStatus, ScopeFilterWithStatus } from '../../lib/InstallationScope'
import { Checkbox } from './Checkbox'
import { toDisplayText } from '../../lib/StringUtilities'

type Props = {
  filters: ScopeFilterWithStatus[]
  onChange?: (filter: ScopeFilterWithStatus) => void
  onAddFilter?: (filter: ScopeFilterWithStatus) => void
  onRemoveFilter?: (filter: ScopeFilterWithStatus) => void
}

export const FilterSummary = ({ filters, onChange, onAddFilter, onRemoveFilter }: Props) => {
  const [freeTextInputs, setFreeTextInputs] = useState<Record<string, string>>({})

  const grouped = React.useMemo(() => {
    const map = new Map<string, ScopeFilterWithStatus[]>()
    for (const f of filters) {
      const key = f.type
      const arr = map.get(key) ?? []
      arr.push(f)
      map.set(key, arr)
    }

    for (const filterType of FREE_TEXT_FILTER_TYPES) {
      if (!map.has(filterType)) {
        map.set(filterType, [])
      }
    }

    const entries = Array.from(map.entries()).sort(([a], [b]) => toDisplayText(a).localeCompare(toDisplayText(b)))

    return entries.map(([type, fs]) => ({
      type: type as FilterType,
      filters: [...fs].sort((x, y) => x.value.localeCompare(y.value)),
    }))
  }, [filters])

  const handleAddFreeTextFilter = (filterType: FilterType, status: NodeStatus.INCLUDED | NodeStatus.EXCLUDED) => {
    const value = freeTextInputs[filterType]?.trim()
    if (!value || !onAddFilter) return

    const existingFilter = filters.find((f) => f.type === filterType && f.value === value)
    if (existingFilter) {
      onChange?.({ ...existingFilter, status })
    } else {
      onAddFilter({ type: filterType, value, status })
    }
    setFreeTextInputs((prev) => ({ ...prev, [filterType]: '' }))
  }

  const isFreeTextFilterType = (type: FilterType): boolean => {
    return FREE_TEXT_FILTER_TYPES.includes(type)
  }

  const leftColumnGroups = grouped.filter((g) => !isFreeTextFilterType(g.type))
  const rightColumnGroups = grouped.filter((g) => isFreeTextFilterType(g.type))

  const renderGroup = (group: { type: FilterType; filters: ScopeFilterWithStatus[] }) => (
    <div key={group.type} className="flex flex-col gap-1.5 mb-2">
      <label className="font-bold capitalize">{toDisplayText(group.type)}</label>

      {group.filters.map((filter) => {
        const filterId = toDisplayText(filter.type) + ': ' + filter.value
        const isInteractive = !!onChange

        return (
          <div key={filterId} className="flex items-center px-[5px] py-[5px] pl-[9px]">
            <Checkbox
              status={filter.status}
              disabled={!isInteractive}
              onChange={() => {
                if (!onChange) return

                switch (filter.status) {
                  case NodeStatus.INCLUDED:
                    onChange({ ...filter, status: NodeStatus.EXCLUDED })
                    break
                  case NodeStatus.EXCLUDED:
                    onChange({ ...filter, status: NodeStatus.NONE })
                    break
                  case NodeStatus.NONE:
                    onChange({ ...filter, status: NodeStatus.INCLUDED })
                    break
                }
              }}
            />
            <span className="capitalize">{filterId}</span>

            {isFreeTextFilterType(group.type) && onRemoveFilter && (
              <button type="button" onClick={() => onRemoveFilter(filter)} className="btn btn-sm btn-secondary ml-auto">
                Remove
              </button>
            )}
          </div>
        )
      })}

      {isFreeTextFilterType(group.type) && onAddFilter && (
        <div className="flex flex-col gap-sm">
          <input
            type="text"
            placeholder={`Enter ${toDisplayText(group.type)} value...`}
            value={freeTextInputs[group.type] ?? ''}
            onChange={(e) => setFreeTextInputs((prev) => ({ ...prev, [group.type]: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleAddFreeTextFilter(group.type, NodeStatus.INCLUDED)
              }
            }}
            className="input"
          />

          <div className="flex gap-sm">
            <button
              type="button"
              disabled={!freeTextInputs[group.type]?.trim()}
              onClick={() => handleAddFreeTextFilter(group.type, NodeStatus.INCLUDED)}
              className="btn btn-primary-dark py-sm"
            >
              Include
            </button>

            <button
              type="button"
              disabled={!freeTextInputs[group.type]?.trim()}
              onClick={() => handleAddFreeTextFilter(group.type, NodeStatus.EXCLUDED)}
              className="btn btn-primary-danger py-sm"
            >
              Exclude
            </button>
          </div>
        </div>
      )}
    </div>
  )

  return (
    <div className="my-4 mx-2 flex items-start gap-4">
      <div className="flex min-w-0 flex-1 flex-col gap-2.5">{leftColumnGroups.map(renderGroup)}</div>
      <div className="flex min-w-0 flex-1 flex-col gap-2.5">{rightColumnGroups.map(renderGroup)}</div>
    </div>
  )
}
